import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { CountUp } from '@/components/ui/count-up';
import { useDocumentHead } from '@/hooks/use-document-head';

const VALUES = [
  { icon: '⚡', title: 'ZERO COMPROMISE', desc: 'Every room. Every time. No shortcuts, no cutting corners.' },
  { icon: '🛡️', title: 'FULLY INSURED', desc: 'Licensed, bonded, and insured. Your property is protected.' },
  { icon: '👥', title: 'OWNER-LED', desc: 'Daniel leads every crew. The owner shows up — not a subcontractor.' },
  { icon: '🏆', title: '100% GUARANTEED', desc: 'Not satisfied? We come back. No questions, no excuses.' },
];

export default function AboutPage() {
  useDocumentHead(
    'About SOTSVC | Sonz of Thunder Services, Orlando',
    'Meet Sonz of Thunder Services (SOTSVC), a locally owned Orlando cleaning company serving homes & businesses across Central Florida.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] clip-diag-bottom overflow-hidden">
        <img
          src="/images/heroes/hero-2-team-action.webp"
          alt="SOTSVC team"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.9) 0%, rgba(15,15,30,.6) 50%, rgba(0,180,216,.2) 100%)' }}
        />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
            <div className="max-w-3xl fade-in">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-[3px] w-16 bg-thunder" />
                <span className="text-thunder font-display tracking-[0.4em] text-xs">// OUR STORY</span>
              </div>
              <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88] mb-6">
                THE <span className="text-thunder">THUNDER</span> STORY
              </h1>
              <p className="text-white/80 text-lg sm:text-xl max-w-xl font-light">
                Built on faith, trained by Coverall, obsessed with excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden noise">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal direction="left">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-electric" />
              <span className="text-electric font-display tracking-[0.4em] text-xs">// THE BEGINNING</span>
            </div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
              FROM <span className="text-thunder">JAN-PRO</span> & <span className="text-thunder">COVERALL</span> TO OWNER OPERATED
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              Daniel Alvarez entered the commercial cleaning industry in 2011, starting with Jan-Pro alongside his cousin — while his brother launched his own Jan-Pro franchise around the same time. That early grind taught him the fundamentals: consistency, accountability, and what it takes to earn a client's trust night after night.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              After Jan-Pro, Daniel transitioned to Coverall, where he spent the next three to five years mastering health-based commercial cleaning — the Core 4 process, color-coded microfiber systems, EPA-registered disinfectants, and hospital-grade protocols. That combined experience across two of the nation's largest cleaning franchises gave him something most independent operators don't have: a real foundation.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              Sonz of Thunder Services is the result — franchise-level quality with the personal touch and accountability of a locally owned business. Headquartered in Altamonte Springs, SOTSVC now serves homes, offices, medical facilities, and post-construction sites across Orlando and Central Florida. Every crew is owner-led. Every job is inspected. Every client gets a handshake and a guarantee.
            </p>
            <div className="inline-flex items-center gap-3 bg-charcoal-mid/50 border border-thunder/40 px-5 py-3">
              <span className="text-thunder text-xl">&#9889;</span>
              <span className="font-display text-thunder tracking-[0.3em] text-xs">YOU'RE CLEAN OR YOU'RE DIRTY</span>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" className="relative">
            <div className="absolute -inset-4 bg-electric/10 blur-3xl" />
            <div className="relative skew-card overflow-hidden border-4 border-electric">
              <div className="skew-inner aspect-[4/5]">
                <img
                  src="/images/heroes/hero-5-solo-mop.webp"
                  alt="Daniel Alvarez - SOTSVC owner"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="font-display font-bold text-[18vw] lg:text-[10rem] text-white/[0.03] whitespace-nowrap">MISSION</div>
        </div>
        <ScrollReveal className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-[2px] w-10 bg-thunder" />
            <span className="text-thunder font-display tracking-[0.4em] text-xs">// WHY WE DO IT</span>
            <span className="h-[2px] w-10 bg-thunder" />
          </div>
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-8">
            CLEAN SPACES. <span className="text-electric">CLEAR MINDS.</span>
          </h2>
          <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto mb-6">
            We believe a clean environment isn't a luxury — it's a necessity. It affects your health, your focus, your productivity, and the impression you make on everyone who walks through the door.
          </p>
          <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto">
            Our mission is simple: deliver the highest standard of cleanliness with transparency, reliability, and a crew that cares as much about your space as you do.
          </p>
        </ScrollReveal>
      </section>

      {/* Values */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-3">// WHAT WE STAND ON</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              OUR <span className="text-thunder">VALUES</span>
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <ScrollReveal key={v.title} delay={i * 100}>
                <div className="bg-charcoal-mid/40 border border-electric/20 p-8 h-full hover:-translate-y-2 transition-transform duration-300">
                  <div className="text-5xl mb-6">{v.icon}</div>
                  <h3 className="font-display font-bold text-white text-xl mb-3 tracking-wider">{v.title}</h3>
                  <p className="text-white/60 leading-relaxed">{v.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-20 bg-charcoal border-y-2 border-electric/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { v: 500, s: '+', label: 'Happy Clients' },
              { v: 10, s: '+', label: 'Years Experience' },
              { v: 12, s: '', label: 'Cities Served' },
              { v: 100, s: '%', label: 'Satisfaction Rate' },
            ].map((stat) => (
              <ScrollReveal key={stat.label}>
                <div className="font-display font-bold text-electric text-6xl sm:text-7xl leading-none mb-3" style={{ textShadow: '0 0 40px rgba(0,180,216,.5)' }}>
                  <CountUp end={stat.v} suffix={stat.s} />
                </div>
                <div className="h-[2px] w-12 bg-thunder mx-auto mb-3" />
                <div className="font-display text-white tracking-[0.25em] text-xs uppercase">{stat.label}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
            READY TO MEET <span className="text-thunder">THE CREW?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
            Get a free walkthrough and quote. We'll show you exactly what we'll do and how we'll do it.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric"
            >
              GET FREE QUOTE →
            </button>
            <a
              href="tel:4074616039"
              className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition"
            >
              CALL (407) 461-6039
            </a>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
